import { hasPermission } from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { GameHero, heroVersion } from '@/components/game-hero';
import { QueryState } from '@/components/query-state';
import { activeServerTab } from '@/components/shell/server-nav';
import { SectionTabs } from '@/components/shell/server-tabs';
import { useServerNav } from '@/components/shell/use-server-nav';
import { accentStyle } from '@/lib/game-icons';
import { startupQuery } from '@/lib/server-queries';
import { useServerLive } from '@/lib/server-socket';

export const Route = createFileRoute('/_app/server/$server')({ component: ServerLayout });

/**
 * A server's frame: back to the list, the hero (name, chips, power) and the current
 * section's tabs. The sections themselves are in the sidebar (and the mobile drawer).
 */
function ServerLayout() {
  const { server: id } = Route.useParams();
  const { query: server, sections } = useServerNav(id);
  const live = useServerLive(server.data && server.data.status !== 'suspended' ? id : null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The hero's version chip reads the startup variables, so only callers who may see them ask.
  const startup = useQuery({
    ...startupQuery(id),
    enabled: Boolean(
      server.data?.profile.identity.versionFrom &&
      hasPermission(server.data.permissions, 'startup.read'),
    ),
  });
  const current = sections ? activeServerTab(sections, pathname) : undefined;
  const running = live.state && live.state !== 'offline';
  return (
    <QueryState query={server}>
      {(s) => (
        <div className="game-accent" style={accentStyle(s.profile.identity)}>
          <Link
            to="/"
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 mb-3 inline-flex items-center gap-1.5 rounded-sm text-sm font-medium transition-colors outline-none focus-visible:ring-[3px]"
          >
            <ArrowLeft className="size-4" aria-hidden /> My Servers
          </Link>
          <GameHero
            server={s}
            state={live.state}
            socket={live.socket}
            version={heroVersion(s.profile.identity, startup.data?.variables)}
            players={running && s.egg.playerQuery ? live.stats?.players : undefined}
          />
          <div className="mt-5 grid gap-6">
            {current && <SectionTabs section={current.section} active={current.tab} server={id} />}
            <Outlet />
          </div>
        </div>
      )}
    </QueryState>
  );
}
